import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Card, CardBody, Button, Input } from '@img/ui';
import { MultiImageUploader } from '../components/ImageUploader';
import { api, type ApiSuccess } from '../lib/api';
import { SEO } from '../components/SEO';

interface SubmitResult { ticketNumber: string; slaDeadline: string }

const TYPES: Array<{ value: string; label: string; emoji: string }> = [
  { value: 'PROPERTY_AD', label: 'إعلان عقاري',     emoji: '🏠' },
  { value: 'AUCTION',     label: 'مزاد',            emoji: '🔨' },
  { value: 'BROKER',      label: 'مسوّق عقاري',     emoji: '🧑‍💼' },
  { value: 'PAYMENT',     label: 'دفع / اشتراك',    emoji: '💳' },
  { value: 'HARASSMENT',  label: 'تحرّش / سلوك',    emoji: '⚠️' },
  { value: 'PRIVACY',     label: 'خصوصية / بيانات', emoji: '🔒' },
  { value: 'COPYRIGHT',   label: 'حقوق ملكية',      emoji: '©' },
  { value: 'OTHER',       label: 'غير ذلك',         emoji: '📄' },
];

/**
 * 📣 صفحة تقديم الشكاوى — متطلب REGA
 * مفتوحة لأي زائر (لا تستلزم تسجيل دخول).
 */
export function ComplaintsPage() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    type: '',
    subject: '',
    description: '',
    reporterName: '',
    reporterEmail: '',
    reporterPhone: '',
    reporterNationalId: '',
    againstUserId: '',
    againstPropertyId: '',
    againstAuctionId: '',
  });
  const [evidence, setEvidence] = useState<string[]>([]);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<SubmitResult | null>(null);

  const m = useMutation({
    mutationFn: () => api.post<ApiSuccess<SubmitResult>>('/api/complaints', {
      ...f,
      reporterPhone: f.reporterPhone || null,
      reporterNationalId: f.reporterNationalId || null,
      againstUserId:     f.againstUserId     || null,
      againstPropertyId: f.againstPropertyId || null,
      againstAuctionId:  f.againstAuctionId  || null,
      evidence: evidence.map((url) => ({ url, type: 'image', name: url.split('/').pop() })),
    }),
    onSuccess: (r) => setResult(r.data),
    onError: (e: Error) => setErr(e.message),
  });

  const validate = (): string | null => {
    if (!f.type)                              return 'اختر نوع الشكوى';
    if (f.subject.trim().length < 5)          return 'الموضوع قصير جداً (٥ أحرف فأكثر)';
    if (f.description.trim().length < 20)     return 'الوصف يجب أن يكون ٢٠ حرفاً فأكثر';
    if (f.reporterName.trim().length < 2)     return 'الاسم مطلوب';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.reporterEmail)) return 'بريد إلكتروني غير صحيح';
    return null;
  };

  const submit = () => {
    const v = validate();
    if (v) { setErr(v); return; }
    setErr('');
    m.mutate();
  };

  // ✓ نجاح
  if (result) {
    return (
      <div className="bg-gradient-to-b from-navy-soft/30 to-white min-h-[70vh] py-12 px-4">
        <Card className="max-w-2xl mx-auto"><CardBody className="text-center py-10">
          <div className="text-6xl mb-3">📥</div>
          <h1 className="text-2xl font-black text-navy-dark mb-2">تم استلام شكواكم</h1>
          <p className="text-gray-600 mb-5">سنرد عليكم خلال ٥ أيام عمل كحد أقصى. تم إرسال تأكيد لإيميلكم.</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 inline-block">
            <div className="text-xs text-gray-500 mb-1">رقم التذكرة (احفظه للمتابعة)</div>
            <div className="text-2xl font-black text-navy-dark tracking-wider" style={{ fontFamily: 'monospace' }}>{result.ticketNumber}</div>
          </div>
          <div className="flex gap-2 justify-center mt-6">
            <Button variant="primary" onClick={() => navigate(`/complaints/track/${result.ticketNumber}`)}>متابعة الحالة</Button>
            <Button variant="ghost" onClick={() => navigate('/')}>الرجوع للرئيسية</Button>
          </div>
        </CardBody></Card>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-navy-soft/30 to-white">
      <SEO
        title="تقديم شكوى — شركة صورة العقارية 📣"
        description="قدّم شكواك على إعلان عقاري أو مزاد أو مسوّق أو دفع. نلتزم بدراسة كل شكوى والرد خلال 5 أيام عمل وفق متطلبات الهيئة العامة للعقار (REGA). شفافية كاملة وحماية للمستهلك. العقار بصورة جديدة."
        keywords="تقديم شكوى عقارية, شكوى على إعلان عقاري, شكوى على مزاد عقاري, شكوى على مسوّق عقاري, شكاوى REGA, حماية المستهلك العقاري, الهيئة العامة للعقار, شركة صورة العقارية, آلية الشكاوى, تتبع الشكوى, ticket عقاري, ضمان الحقوق العقارية, العقار بصورة جديدة"
        canonical="https://img.sa/complaints"
        jsonLd={{
          '@type': 'WebPage',
          name: 'تقديم شكوى — شركة صورة العقارية',
          url: 'https://img.sa/complaints',
          about: { '@type': 'Service', name: 'خدمة تقديم الشكاوى العقارية' },
        }}
      />
      <section className="bg-gradient-to-l from-navy-darker via-navy-dark to-navy text-white py-14 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-6xl mb-3">📣</div>
          <h1 className="text-3xl md:text-4xl font-black mb-3">تقديم شكوى</h1>
          <p className="text-gold-light text-base leading-relaxed max-w-2xl mx-auto">
            صوتك يهمّنا. نلتزم بدراسة كل شكوى والرد رسمياً خلال <b>٥ أيام عمل</b> كحد أقصى.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-5">
        {/* اختيار النوع */}
        <Card><CardBody>
          <h2 className="font-black text-navy-dark mb-3">١. نوع الشكوى *</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setF({ ...f, type: t.value })}
                className={`p-3 rounded-xl border-2 text-sm font-bold transition ${
                  f.type === t.value
                    ? 'border-navy bg-navy text-white'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-navy'
                }`}
              >
                <div className="text-2xl mb-1">{t.emoji}</div>
                {t.label}
              </button>
            ))}
          </div>
        </CardBody></Card>

        {/* تفاصيل الشكوى */}
        <Card><CardBody className="space-y-4">
          <h2 className="font-black text-navy-dark">٢. تفاصيل الشكوى</h2>
          <Input
            label="الموضوع *"
            value={f.subject}
            onChange={(e) => setF({ ...f, subject: e.target.value })}
            placeholder="عنوان مختصر للشكوى"
          />
          <div>
            <label className="block text-sm font-bold text-ink mb-1">الوصف التفصيلي *</label>
            <textarea
              value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })}
              rows={6}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
              placeholder="اشرح ما حصل بتفاصيل دقيقة (التاريخ، الأشخاص، الأرقام، المبالغ...)"
            />
            <p className="text-xs text-gray-500 mt-1">٢٠ حرفاً فأكثر — كلما زادت التفاصيل أسرعنا في المعالجة.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input label="رقم العقار (اختياري)"   value={f.againstPropertyId} onChange={(e) => setF({ ...f, againstPropertyId: e.target.value })} dir="ltr" />
            <Input label="رقم المزاد (اختياري)"    value={f.againstAuctionId}  onChange={(e) => setF({ ...f, againstAuctionId: e.target.value })} dir="ltr" />
            <Input label="رقم المسوّق (اختياري)"  value={f.againstUserId}     onChange={(e) => setF({ ...f, againstUserId: e.target.value })} dir="ltr" />
          </div>
        </CardBody></Card>

        {/* أدلة */}
        <Card><CardBody>
          <h2 className="font-black text-navy-dark mb-2">٣. الأدلة (اختياري)</h2>
          <p className="text-xs text-gray-500 mb-3">صور للإيصالات، المراسلات، لقطات شاشة... حتى ١٠ ملفات.</p>
          <MultiImageUploader
            values={evidence}
            onChange={setEvidence}
            purpose="complaint"
            max={10}
            label="ارفع صور الأدلة"
          />
        </CardBody></Card>

        {/* بياناتك */}
        <Card><CardBody className="space-y-3">
          <h2 className="font-black text-navy-dark">٤. بياناتك للتواصل</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="الاسم الكامل *"        value={f.reporterName}       onChange={(e) => setF({ ...f, reporterName: e.target.value })} />
            <Input label="البريد الإلكتروني *"    value={f.reporterEmail}      onChange={(e) => setF({ ...f, reporterEmail: e.target.value })} dir="ltr" type="email" />
            <Input label="رقم الجوال (اختياري)"   value={f.reporterPhone}      onChange={(e) => setF({ ...f, reporterPhone: e.target.value })} dir="ltr" placeholder="+9665..." />
            <Input label="رقم الهوية (اختياري)"   value={f.reporterNationalId} onChange={(e) => setF({ ...f, reporterNationalId: e.target.value })} dir="ltr" />
          </div>
          <p className="text-xs text-gray-500">بياناتك محفوظة بسرّية تامة وفق سياسة الخصوصية ولا تُشارَك إلا حسب ما يقتضي النظام.</p>
        </CardBody></Card>

        {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">⛔ {err}</div>}

        <div className="flex justify-end">
          <Button variant="primary" size="lg" onClick={submit} loading={m.isPending}>
            📤 إرسال الشكوى
          </Button>
        </div>

        {/* روابط مساعدة */}
        <Card><CardBody>
          <div className="text-xs text-gray-600 leading-relaxed">
            <b>قنوات بديلة:</b> تقدر التواصل معنا على{' '}
            <a href="mailto:info@img.sa" className="text-navy hover:underline" dir="ltr">info@img.sa</a>{' '}
            أو على هاتف{' '}
            <a href="tel:0114993666" className="text-navy hover:underline" dir="ltr">0114993666</a>.
            <br/>
            <b>للحالات العاجلة المتعلقة ببلاغات احتيال:</b> اتصل على <b>911</b> ثم بلّغنا برقم التذكرة.
          </div>
        </CardBody></Card>
      </div>
    </div>
  );
}
